import {
  evaluatePartnerGuidelines,
  getTemplateResponse,
  getTemplateResponseDSCR,
} from "../../services/network";
import { ProductType, SizingTypeValue } from "../../types/enums";
import {
  evaluation,
  getCookie,
  getFormattedDate,
  normalizeEvaluationResult,
  // getTemplateResponseDSCR,
  // getTemplateResponse,
} from "../Common"; // Example utilities
import { sampleCondoEligibility } from "../../pages/Loans/v1/Common";
import {
  labelFieldPricingMapping,
  labelFieldBridgePriceMapping,
  getLeverageAdjustedMaxFieldMapping,
  parseLeverageOverridePercentage,
} from "../mappings";
import { intialDetailedLoanSizingState } from "../../context/LoansContext";
import { getOverriddenTestResults } from "../../components/waivers/utils";

function hasOnlySpaces(input: string) {
  return /^\s*$/.test(input);
}

const getFlastRequestBodyForGuidelines = (props: any) => {};

// Shared by both the quick and detailed loan-sizing branches below: derives
// the borrower "experience" value, discarding it (falling back to null)
// whenever it's blank, decimal-looking, or absent.
function computeExperience(borrowerInformation: any): any {
  let experience =
    borrowerInformation?.experience ||
    borrowerInformation?.propertiesCompletedWithinLast36Months ||
    borrowerInformation?.borrowerGUCExperience;
  if (
    hasOnlySpaces(experience) ||
    experience?.includes(".") ||
    !experience
  ) {
    experience = null;
  }
  return experience;
}

// Shared by both the quick and detailed loan-sizing branches below: a
// foreign/blank citizenship status is normalized to "Yes", otherwise the
// citizenship status itself is passed through unchanged.
function computeForeignNationalStatus(citizenshipStatus: any): any {
  if (
    citizenshipStatus?.includes("Foriegn") ||
    citizenshipStatus === ""
  ) {
    return "Yes";
  }
  return citizenshipStatus;
}

// loanEcon.interestRate must be null or a number, never "" — the stored
// value comes from handleInterestChange's (rate * 100).toFixed(3), which is
// a string, and an unset field defaults to "" in the form state rather than
// null/undefined.
function normalizeInterestRateForApi(value: any): number | null {
  return value !== undefined && value !== null && value !== "" ? Number(value) : null;
}

// Builds the loanObj/propObj pair for the QUICK_LOAN_SIZING branch of
// getRequestBodyForGuidelines.
function buildQuickLoanSizingBranch(
  quickLoanSizingData: any,
  loanDetails: any,
  dynamicConfig: any,
  loanId: any
): { loanObj: any; propObj: any } {
  const experience = computeExperience(
    quickLoanSizingData?.borrowerInformation
  );

  const foreignNational = computeForeignNationalStatus(
    quickLoanSizingData?.borrowerInformation?.citizenshipStatus
  );
  // } else if (
  //   quickLoanSizingData?.borrowerInformation?.borrowerGuarantor === "No"
  // ) {
  //   foreignNational = "No";
  // }

  const loanObj = {
    loanInfo: {
      primaryLoanId: quickLoanSizingData?.loanInformation?.originatorLoanID,
      // loanPurpose: quickLoanSizingData?.loanInformation?.loanPurpose ?? "",
      originalMaturityDate:
        quickLoanSizingData?.loanInformation?.originalMaturityDate || null,
      crossCollaterlization: null,
      toorakProduct:
        quickLoanSizingData?.loanInformation?.toorakProduct || "Rehab",
      loanType:
        loanDetails?.productType === ProductType?.DSCR
          ? "30Year"
          : "Bridge",
      product: quickLoanSizingData?.loanInformation?.product || "RENTAL",
      loanStage: "FES",
      loanId,
      condoEligibility:
        quickLoanSizingData?.loanInformation?.condoEligibility || "N/A",
      cashOutFlag: quickLoanSizingData?.loanInformation?.cashOutFlag || "",
      borrowerProceeds:
        quickLoanSizingData?.loanInformation?.borrowerProceeds ||
        quickLoanSizingData?.loanInformation?.cashOutRefinance ||
        "Y",
      predominantPropertyType:
        quickLoanSizingData?.loanInformation?.predominantPropertyType || "",
      predominantState:
        quickLoanSizingData?.loanInformation?.predominantState || "",
      exitStrategy:
        quickLoanSizingData?.loanInformation?.exitStrategy || "",
      loanTerm:
        quickLoanSizingData?.loanInformation?.loanTerm?.toString() || "360",
      expectedClosingDate:
        quickLoanSizingData?.loanInformation?.expectedClosingDate ? getFormattedDate(quickLoanSizingData?.loanInformation?.expectedClosingDate) : null,
      creditEvent:
        quickLoanSizingData?.loanInformation?.creditEvent || "No",
      originationDate:
        quickLoanSizingData?.loanInformation?.expectedClosingDate ? getFormattedDate(quickLoanSizingData?.loanInformation?.expectedClosingDate) : null,
    },
    loanEcon: {
      originalLoanAmount:
        quickLoanSizingData?.loanInformation?.borrowerRequested ||
        quickLoanSizingData?.loanInformation?.loanAmount ||
        quickLoanSizingData?.loanInformation?.borrowerRequested ||
        null,
      // Whichever rate was last selected (a buy-down pick or a CC edit) —
      // an approved pricing exception adds discount points on top of this
      // rate rather than changing it (see
      // calculateSubtractionOfCurrentSelectedInterestRate), so this same
      // field already reflects the right rate for that case too. Was
      // hardcoded null, so the partner API never received any rate here
      // regardless of what the user had selected.
      interestRate: normalizeInterestRateForApi(
        quickLoanSizingData?.loanEconomics?.interestRate,
      ),
      totalOriginationAndDiscountPoints:
        quickLoanSizingData?.loanInformation
          ?.totalOriginationAndDiscountPoints || 0.0,
      rateType: quickLoanSizingData?.loanInformation?.rateType || "Fixed",
      prePaymentPenaltyMonths:
        quickLoanSizingData?.loanEconomics?.prepaymentPenaltyTypeMonths ??
        null,
      prepayPenaltyType:
        prePayTypeToMonthsMap?.[
          quickLoanSizingData?.loanEconomics?.prepaymentPenaltyTypeMonths ??
            "0"
        ] || null,
      interestOnlyPeriod: (quickLoanSizingData?.loanEconomics?.ioPeriod ?? 0) || 0,
      armIndex: quickLoanSizingData?.loanInformation?.armIndex ?? "0",
      grossMarginForArm:
        quickLoanSizingData?.loanInformation?.grossMarginForArm ?? 0.0,
      includeOutOfPocketBudgetInARLTV:
        quickLoanSizingData?.loanInformation
          ?.includeOutOfPocketBudgetInARLTV ?? true,
      // monthlyPropertyGrossRent:
      //   quickLoanSizingData?.propertyEconomics?.monthlyPropertyGrossRent,
      // armType: quickLoanSizingData?.loanEconomics?.armType ?? null,
      // armIndex: quickLoanSizingData?.loanEconomics?.armIndex ?? null,
      // grossArmMargin: quickLoanSizingData?.loanEconomics?.grossArmMargin ?? null,
      // initialRateAdjustmentDate: quickLoanSizingData?.loanEconomics?.initialRateAdjustmentDate ?? null,
      // initialPayAdjustmentDate: quickLoanSizingData?.loanEconomics?.initialPayAdjustmentDate ?? null,
      // initialPeriodicCap: quickLoanSizingData?.loanEconomics?.initialPeriodicCap ?? null,
      // subsequentPeriodicCap: quickLoanSizingData?.loanEconomics?.subsequentPeriodicCap ?? null,
      // lifeRateCap: quickLoanSizingData?.loanEconomics?.lifeRateCap ?? null,
      // lifeTimeFloor: quickLoanSizingData?.loanEconomics?.lifeTimeFloor ?? null
    },
    loanDetail: {
      originatorPartyId:
        loanDetails?.orgId || dynamicConfig?.clientId || getCookie("orgId"),
    },
    loanUserMap: [
      {
        isPrimary: true,
        loanUserSequence: "1",
        loanUserType: "Borrower",
        originalCreditScoreMedian:
          quickLoanSizingData?.borrowerInformation?.creditScoreActual ||
          null,
        experience,
        customer: {
          foreignNationalString:
            quickLoanSizingData?.borrowerInformation?.citizenshipStatus,
          partyType:
            quickLoanSizingData?.borrowerInformation?.borrowerType ===
            "Entity"
              ? "account"
              : "person",
        },
        heavyRehabExperience: null,
        borrowerGUCExperience: null,
      },
    ],
  };
  const propObj = {
    propertyLocation: {
      ...quickLoanSizingData?.propertyLocation,
      city: quickLoanSizingData?.propertyLocation?.city || "",
      state: quickLoanSizingData?.propertyLocation?.state || "",
      postalCode: quickLoanSizingData?.propertyLocation?.postalCode || "",
      propertyDesignation: quickLoanSizingData?.propertyLocation?.propertyDesignation || "",
    },
    propertyInfo: {
      propertyType: quickLoanSizingData?.propertyInformation?.propertyType,
      numberOfUnits:
        quickLoanSizingData?.propertyInformation?.numberOfUnits ?? 1,
      propertyAcquisitionDate:
        quickLoanSizingData?.propertyInformation?.originalDateOfAcquisition ?
            getFormattedDate(
              quickLoanSizingData?.propertyInformation?.originalDateOfAcquisition
            ) : null
    },
    propertyEcon: {
      // ...quickLoanSizingData?.propertyEconomics,
      originalAsIsAppraisalValue:
        quickLoanSizingData?.propertyInformation?.asIsValue,
      purchasePrice: quickLoanSizingData?.loanInformation?.purchasePrice,
      acquisitionPrice: quickLoanSizingData?.loanInformation?.purchasePrice,
      annualPropertyTaxes:
        quickLoanSizingData?.propertyEconomics?.annualPropertyTaxes,
      annualFloodInsurance:
        quickLoanSizingData?.propertyEconomics?.annualInsurance,
      annualHOAFee: quickLoanSizingData?.propertyEconomics?.annualHOAFee,
      verifiedCostsAndImprovements: quickLoanSizingData?.propertyEconomics?.verifiedCostsAndImprovements ? quickLoanSizingData?.propertyEconomics?.verifiedCostsAndImprovements : null,
      // insurance: quickLoanSizingData?.propertyEconomics?.annualInsurance,
    },
    propertyId: 1,
    propertySummary: {
      monthlyTaxAmount: null,
      monthlyInsuranceAmount: null,
      escrowTaxes: null,
      escrowInsurance: null,
    },
  };
  return { loanObj, propObj };
}

// Builds one entry of propObj (the array of per-property request-body
// objects) for the DETAILED_LOAN_SIZING branch of
// getRequestBodyForGuidelines — one call per detaileLoanDataToBePicked
// propertyDetails entry.
function buildDetailedPropertyObject(property: any, pId: number): any {
  const {
    propertyInformation,
    propertyLocation,
    unitInformation,
    propertyEconomics,
  } = property;
  let closingCost = "0";
  if (propertyEconomics?.closingCosts) {
    closingCost = (
      +propertyEconomics?.closingCosts?.split("%")[0] / 100
    ).toFixed(4);
  }

  const returnObj = {
    propertyId: pId + 1,
    propertySummary: {},
    propertyInfo: {
      propertyType: propertyInformation?.propertyType || "",
      numberOfUnits: propertyInformation?.numberOfUnits
        ? hasOnlySpaces(propertyInformation?.numberOfUnits) ||
          propertyInformation?.numberOfUnits?.includes(".")
          ? 0
          : propertyInformation?.numberOfUnits
          ? +propertyInformation?.numberOfUnits
          : 1
        : 1,
      mostRecentPropertyValuationDate:
        getFormattedDate(
          propertyInformation?.mostRecentPropertyValuationDate
        ) || null,
      propertyAcquisitionDate:
        propertyInformation?.originalDateOfAcquisition ?
        getFormattedDate(
          propertyInformation?.originalDateOfAcquisition
        ) : null,
      squareFootage:
        propertyInformation?.propertySqftFootage?.replaceAll(",", ""),
      preRehabSquareFootage:
        propertyInformation?.propertySqftFootage?.replaceAll(",", ""),
      postRehabSquareFootage: 0.0,
      isPurchasedWithDebt: propertyInformation?.propertyWithDebt,
      isPropertyRecentlyRenovated: propertyInformation?.isPropertyRecentlyRenovated,
    },
    propertyEcon: {
      originalAsIsAppraisalValue: propertyEconomics?.asIsValue || null,
      purchasePrice:
        propertyEconomics?.propertyAcquisitionPrice ||
        propertyEconomics?.purchasePrice ||
        null,
      acquisitionPrice:
        propertyEconomics?.propertyAcquisitionPrice ||
        propertyEconomics?.purchasePrice ||
        null,
      costBasisAfterCostIncurred:
        (+closingCost + 1) * propertyEconomics?.purchasePrice,
      annualPropertyTaxes:
        propertyEconomics?.annualPropertyTaxes ?? null,
      annualHazardInsurance:
        propertyEconomics?.annualHazardInsurance ?? null,

      // insurance: propertyEconomics?.annualInsurance ?? null,
      // marketRentPerMonth:
      //   propertyEconomics?.grossPotentialRentMonthly ?? null,
      annualHOAFee: propertyEconomics?.annualHOAFee ?? null,
      annualFloodInsurance:
        propertyEconomics?.annualFloodInsurance ?? null,

      thirdPartyValuation:
        propertyEconomics?.thirdPartyValuation ?? null,
      thirdPartyValuationStatus:
        propertyEconomics?.thirdPartyValuationStatus ?? null,
      closingCost,
      additionalEligibleCost: null,
      decliningMarkets: propertyEconomics?.decliningMarkets ?? null,
      verifiedCostsAndImprovements: propertyEconomics?.verifiedCostsAndImprovements ? propertyEconomics?.verifiedCostsAndImprovements : null,

    },
    propertyLocation: {
      city: propertyLocation?.city || "",
      state: propertyLocation?.state || "",
      postalCode: propertyLocation?.postalCode || "",
      propertyDesignation: propertyLocation?.propertyDesignation || "",
    },
    units: unitInformation?.map((unit: any, uId: number) => {
      return {
        propertyUnitId: uId + 1,
        currentLeaseTermMonths: +unit?.currentLeaseTermsInMonths,
        leaseStatus: unit?.rentalCharacterization || "",
        unitOccupancy: unit?.rentalCharacterization?.includes(
          "Occupied"
        )
          ? 1
          : 0,
        marketRentMonthly: unit?.monthlyMarketRent ? unit?.monthlyMarketRent : null,
        inPlaceLeaseRentMonthly: unit?.inPlaceLeaseRentMonthly ? unit?.inPlaceLeaseRentMonthly : null,
      };
    }),
  };
  return returnObj;
}

// Builds the loanObj/propObj pair for the DETAILED_LOAN_SIZING branch of
// getRequestBodyForGuidelines.
function buildDetailedLoanSizingBranch(
  detaileLoanDataToBePicked: any,
  loanDetails: any,
  dynamicConfig: any,
  loanId: any
): { loanObj: any; propObj: any } {
  const isRateTypeFixed = detaileLoanDataToBePicked?.loanEconomics?.rateType === "Fixed";

  const experience = computeExperience(
    detaileLoanDataToBePicked?.borrowerInformation
  );

  let foreignNational =
    detaileLoanDataToBePicked?.borrowerInformation?.citizenshipStatus;

  foreignNational = computeForeignNationalStatus(
    detaileLoanDataToBePicked?.borrowerInformation?.citizenshipStatus
  );

  const loanObj = {
    loanInfo: {
      primaryLoanId:
        detaileLoanDataToBePicked?.loanInformation?.originatorLoanID,
      // loanPurpose:
      //   detaileLoanDataToBePicked?.loanInformation?.loanPurpose ?? "",
      originalMaturityDate:
        getFormattedDate(
          detaileLoanDataToBePicked?.loanInformation?.originalMaturityDate
        ) || null,
      crossCollaterlization: null,
      toorakProduct:
        detaileLoanDataToBePicked?.loanInformation?.toorakProduct ||
        "Rehab",
      loanType:
        loanDetails?.productType === ProductType?.DSCR
          ? "30YEAR"
          : "Bridge",
      product:
        detaileLoanDataToBePicked?.loanInformation?.product || "RENTAL",
      loanStage: "FES",
      loanId,
      condoEligibility:
        detaileLoanDataToBePicked?.loanInformation?.condoEligibility ||
        "N/A",
      // cashOutFlag:
      //   detaileLoanDataToBePicked?.loanInformation?.cashOutFlag || "",
      borrowerProceeds:
        detaileLoanDataToBePicked?.loanInformation?.borrowerProceeds ||
        detaileLoanDataToBePicked?.loanInformation?.cashOutRefinance ||
        "Y",
      predominantPropertyType:
        detaileLoanDataToBePicked?.loanInformation
          ?.predominantPropertyType || null,
      predominantState:
        detaileLoanDataToBePicked?.loanInformation?.predominantState || "",
      // exitStrategy:
      //   detaileLoanDataToBePicked?.loanInformation?.exitStrategy || "",
      loanTerm:
        detaileLoanDataToBePicked?.loanInformation?.loanTerm?.toString() ||
        "360",
      expectedClosingDate: detaileLoanDataToBePicked?.loanInformation?.expectedClosingDate ?
        getFormattedDate(
          detaileLoanDataToBePicked?.loanInformation?.expectedClosingDate
        ) : null,
      creditEvent:
        detaileLoanDataToBePicked?.loanInformation?.creditEvent || "No",
      originationDate: detaileLoanDataToBePicked?.loanInformation?.expectedClosingDate ?
        getFormattedDate(
          detaileLoanDataToBePicked?.loanInformation?.expectedClosingDate
        ) : null,
    },
    loanEcon: {
      originalLoanAmount:
        detaileLoanDataToBePicked?.loanEconomics?.borrowerRequested ||
        detaileLoanDataToBePicked?.loanEconomics?.loanAmount ||
        null,
      // Whichever rate was last selected (a buy-down pick or a CC edit) —
      // an approved pricing exception adds discount points on top of this
      // rate rather than changing it (see
      // calculateSubtractionOfCurrentSelectedInterestRate), so this same
      // field already reflects the right rate for that case too. Was
      // hardcoded null, so the partner API never received any rate here
      // regardless of what the user had selected.
      interestRate: normalizeInterestRateForApi(
        detaileLoanDataToBePicked?.loanEconomics?.interestRate,
      ),
      // totalOriginationAndDiscountPoints:
      //   detaileLoanDataToBePicked?.loanEconomics
      //     ?.totalOriginationAndDiscountPoints || 0.0,
      rateType:
        detaileLoanDataToBePicked?.loanEconomics?.rateType || "Fixed",
      prePaymentPenaltyMonths:
        detaileLoanDataToBePicked?.loanEconomics
          ?.prepaymentPenaltyTypeMonths ?? null,
      prepayPenaltyType:
      detaileLoanDataToBePicked?.loanEconomics?.prepayPenaltyType || detaileLoanDataToBePicked?.loanEconomics?.prepayPenaltyType === 0 ?
        `${detaileLoanDataToBePicked?.loanEconomics?.prepayPenaltyType}` : null,
      interestOnlyPeriod:
        (detaileLoanDataToBePicked?.loanEconomics?.ioPeriod ?? 0) || 0,
      // armIndex: detaileLoanDataToBePicked?.loanEconomics?.armIndex ?? "0",
      // grossMarginForArm:
      //   detaileLoanDataToBePicked?.loanEconomics?.grossMarginForArm ?? 0.0,
      // includeOutOfPocketBudgetInARLTV:
      //   detaileLoanDataToBePicked?.loanEconomics
      //     ?.includeOutOfPocketBudgetInARLTV ?? true,
      armType: isRateTypeFixed ? null : detaileLoanDataToBePicked?.loanEconomics?.armType || null,
      armIndex: isRateTypeFixed ? null : detaileLoanDataToBePicked?.loanEconomics?.armIndex || "1 Month Term SOFR",
      // grossArmMargin: detaileLoanDataToBePicked?.loanEconomics?.grossArmMargin ?? null,
      initialRateAdjustmentDate: isRateTypeFixed ? null : getFormattedDate(detaileLoanDataToBePicked?.loanEconomics?.initialRateAdjustmentDate) || null,
      initialPayAdjustmentDate: isRateTypeFixed ? null : getFormattedDate(detaileLoanDataToBePicked?.loanEconomics?.initialPayAdjustmentDate) || null,
      // initialPeriodicCap: detaileLoanDataToBePicked?.loanEconomics?.initialPeriodicCap ?? null,
      // subsequentPeriodicCap: detaileLoanDataToBePicked?.loanEconomics?.subsequentPeriodicCap ?? null,
      // lifeRateCap: detaileLoanDataToBePicked?.loanEconomics?.lifeRateCap ?? null,
      // lifeTimeFloor: detaileLoanDataToBePicked?.loanEconomics?.lifeTimeFloor ?? null
    },
    loanDetail: {
      originatorPartyId:
        loanDetails?.orgId || dynamicConfig?.clientId || getCookie("orgId"),
    },
    loanUserMap: [
      {
        isPrimary: true,
        loanUserSequence: "1",
        loanUserType: "Borrower",
        originalCreditScoreMedian:
          detaileLoanDataToBePicked?.borrowerInformation
            ?.creditScoreActual || null,
        experience,
        customer: {
          foreignNationalString: foreignNational,
          partyType:
            detaileLoanDataToBePicked?.borrowerInformation?.borrowerType ===
            "Entity"
              ? "account"
              : "person",
        },
        firstTimeHomeBuyer: detaileLoanDataToBePicked?.borrowerInformation?.firstTimeHomeBuyer ?? null
        // heavyRehabExperience: null,
        // borrowerGUCExperience: null,
      },
    ],
  };
  const propObj: any = [];
  detaileLoanDataToBePicked?.propertyDetails?.map(
    (property: any, pId: number) => {
      const returnObj = buildDetailedPropertyObject(property, pId);
      propObj.push(returnObj);
    }
  );
  return { loanObj, propObj };
}

// Computes the loanPricing object (finalToorakPrice/actualInterestRate) from
// loanEconomicsForPricing for getRequestBodyForGuidelines.
function resolveLoanPricing(loanEconomicsForPricing: any): any {
  const loanPricing: any = {};
  if (loanEconomicsForPricing?.finalToorakPrice) {
    loanPricing.finalToorakPrice = +loanEconomicsForPricing.finalToorakPrice;
  }
  if (loanEconomicsForPricing?.interestRate) {
    loanPricing.actualInterestRate = +loanEconomicsForPricing.interestRate / 100;
  }
  return loanPricing;
}

// Each Pricing-card row is only ever identified in the UI by its live
// display label (see LeverageTable.tsx — there's no stable id in the API
// response to key off). labelFieldPricingMapping (DSCR) /
// labelFieldBridgePriceMapping (Bridge) (src/utils/mappings.ts) are the
// existing label -> backend field name lookups already used to build this
// same loanPricing.<field> tooltip/infoContent path for reading these
// values — reused here so an edited row is sent back under the field name
// the rules engine actually recognizes (mirroring actualInterestRate/
// finalToorakPrice above), not the raw label text. A row's label only ever
// matches one of the two mappings (they don't share labels), so checking
// both is safe regardless of which loan type this request is for.
function applyPricingAdjustmentOverrides(
  loanPricing: any,
  pricingAdjustmentOverrides?: Record<string, string>
): void {
  Object.entries(pricingAdjustmentOverrides ?? {}).forEach(([rowLabel, value]) => {
    const field =
      labelFieldPricingMapping.find((m) => m.label === rowLabel)?.field ??
      labelFieldBridgePriceMapping.find((m) => m.label === rowLabel)?.field;
    if (field) {
      loanPricing[field] = value;
    }
  });
}

// Unlike every other resultObject field this mapping can target
// (finalLeverage.afterAdjustmentMaxLTVAsIs, ...), the rules engine expects
// toorakProceeds.toorakAdjustedAsIsLtv/toorakAdjustedAsIsLtc as a fraction
// (0.33) rather than a plain percentage (33) — matching how
// loanPricing.actualInterestRate is sent as 0.0625, not 6.25, elsewhere in
// this same resultObject.
const FIELDS_SENT_AS_FRACTION = new Set([
  "toorakAdjustedAsIsLtv",
  "toorakAdjustedAsIsLtc",
]);

// Same override-first pattern as loanPricing above, for the Leverages
// card's "Adjusted Max" row. getLeverageAdjustedMaxFieldMapping() has no
// loanStage to key off here (this call site always evaluates with the
// same DSCR-shaped fetchData getLeverageTableBody itself defaults to — see
// getTestTabResults below), so it falls back to the DSCR column mapping,
// matching exactly what the Leverages card actually displays today.
function computeLeverageAdjustments(
  leverageAdjustedMaxOverrides?: Record<string, string>
): any {
  const leverageAdjustments: any = {};
  Object.entries(leverageAdjustedMaxOverrides ?? {}).forEach(([column, value]) => {
    const mapping = getLeverageAdjustedMaxFieldMapping().find((m) => m.column === column);
    if (mapping) {
      // Sent as a bare number (23, 1.23) — see parseLeverageOverridePercentage
      // for why the stored "<number> %" string can't go to the rules
      // engine as-is.
      const parsedValue = parseLeverageOverridePercentage(value);
      leverageAdjustments[mapping.section] = {
        ...leverageAdjustments[mapping.section],
        // toorakProceeds.toorakAdjustedAsIsLtv/toorakAdjustedAsIsLtc (and
        // finalLeverage's equivalents) are fractions everywhere else the
        // rules engine reads/returns them (0.75, not 75) — see
        // getPercentageValue's *100 in getLeverageTableBody, which is what
        // turns that fraction into the "75.00 %" this row displays. The
        // stored override string is the display-shaped "<number> %" (e.g.
        // "75.00 %"), so it has to come back down to a fraction the same
        // way before it can stand in for that field in the guideline
        // request — sending the bare 75 here makes the engine echo 75 back
        // as the "fraction", which then redisplays as 7500.00 %.
        [mapping.field]: parseLeverageOverridePercentage(value) / 100,
      };
    }
  });
  return leverageAdjustments;
}

// Builds the resultObject passed to the rules engine for
// getRequestBodyForGuidelines.
function buildResultObj(
  detaileLoanDataToBePicked: any,
  loanId: any,
  loanPricing: any,
  leverageAdjustments: any
): any {
  return {
    loanResult: {
      [loanId]: {
        ...(detaileLoanDataToBePicked?.loanInformation?.loanPurpose ? {
          loanCharacterization: {
            finalLoanCharacterization: detaileLoanDataToBePicked?.loanInformation?.loanPurpose
          }
        } : {}),
        ...(Object.keys(loanPricing).length ? { loanPricing } : {}),
        ...leverageAdjustments,
      },
    },
    propertiesResults: {},
  };
}

function applyQuickLoanCharacterization(
  resultObj: any,
  quickLoanSizingData: any,
  loanDetails: any,
  sizingType: string,
  loanId: any
): void {
  if (
    quickLoanSizingData?.loanInformation?.loanPurpose &&
    (loanDetails?.sizingType || sizingType) ===
      SizingTypeValue.QUICK_LOAN_SIZING
  ) {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      loanCharacterization: {
        finalLoanCharacterization:
          quickLoanSizingData?.loanInformation?.loanPurpose ?? null,
      },
    };
  }
}

function applyQuickPropertyGrossRent(
  resultObj: any,
  quickLoanSizingData: any,
  loanDetails: any
): void {
  if (
    quickLoanSizingData?.propertyEconomics?.monthlyPropertyGrossRent &&
    loanDetails?.sizingType === SizingTypeValue.QUICK_LOAN_SIZING
  ) {
    resultObj.propertiesResults = {
      ...resultObj.propertiesResults,
      "1": {
        loanEconomics: {
          monthlyPropertyGrossRent:
            quickLoanSizingData?.propertyEconomics?.monthlyPropertyGrossRent,
        },
      },
    };
  }
}

function applyTestOverrides(
  resultObj: any,
  loanId: any,
  testOverridesToApply?: Record<string, Record<string, string>>
): void {
  Object.entries(testOverridesToApply ?? {}).forEach(([testType, tests]) => {
    resultObj.loanResult[loanId] = {
      ...resultObj.loanResult[loanId],
      [testType]: {
        ...resultObj.loanResult[loanId]?.[testType],
        ...tests,
      },
    };
  });
}

const getRequestBodyForGuidelines = (
  loanDetails: any,
  quickLoanSizingData: any,
  detailedLoanSizingData: any,
  loanId: any,
  dynamicConfig: any,
  sizingType: string,
  isFlat?: boolean,
  isInitialLoad?: boolean,
  // Caller-supplied so this function never decides on its own whether a
  // waiver override is still warranted — see fetchGuidelinesAndBuyDownOptions,
  // which only passes overrides for tests its natural (unwaived) evaluation
  // still shows failing. That keeps a test that now genuinely passes from
  // being masked as WAIVED forever by a stale, blindly-reapplied override.
  testOverridesToApply?: Record<string, Record<string, string>>,
  // Manual per-row overrides for the "Pricing" adjustments card, keyed by
  // row label (see LeverageTable.tsx / LoansPagev2's
  // handleSavePricingAdjustmentRow). Cleared the moment any other field is
  // edited (clearStalePricingOverrides), same lifecycle as
  // interestRate/finalToorakPrice below.
  pricingAdjustmentOverrides?: Record<string, string>,
  // Manual override for the "Adjusted Max" row of the Leverages card, keyed
  // by column header label ("ltvAsIs" / "ltc" / ...) — see
  // LoansPagev2's handleSaveLeveragesAdjustmentRow. Same lifecycle as
  // pricingAdjustmentOverrides above.
  leverageAdjustedMaxOverrides?: Record<string, string>
) => {
  let loanObj: any = {};
  let propObj: any = [];
  if (isFlat) {
    const data = getFlastRequestBodyForGuidelines({
      loanDetails,
      quickLoanSizingData,
      detailedLoanSizingData,
    });
    return data;
  }
  const detaileLoanDataToBePicked = isInitialLoad
        ? loanDetails
        : detailedLoanSizingData;
  switch (loanDetails?.sizingType || sizingType) {
    case SizingTypeValue.QUICK_LOAN_SIZING: {
      const quickBranch = buildQuickLoanSizingBranch(
        quickLoanSizingData,
        loanDetails,
        dynamicConfig,
        loanId
      );
      loanObj = quickBranch.loanObj;
      propObj = quickBranch.propObj;
      break;
    }
    case SizingTypeValue.DETAILED_LOAN_SIZING: {
      const detailedBranch = buildDetailedLoanSizingBranch(
        detaileLoanDataToBePicked,
        loanDetails,
        dynamicConfig,
        loanId
      );
      loanObj = detailedBranch.loanObj;
      propObj = detailedBranch.propObj;
      break;
    }
    default:
      break;
  }

  // The credit committee edits loanEconomics.interestRate/finalToorakPrice
  // directly on detailedLoanSizingData. For the overridden (waiver-reapplied)
  // guideline check, price against exactly what they entered rather than
  // whatever detaileLoanDataToBePicked resolved to — which is the
  // server-returned/output loan record (loanDetails) when isInitialLoad is
  // true. The natural check is unaffected and keeps using
  // detaileLoanDataToBePicked.
  const isOverriddenGuidelineCheck = Boolean(
    testOverridesToApply && Object.keys(testOverridesToApply).length
  );
  const loanEconomicsForPricing = isOverriddenGuidelineCheck
    ? detailedLoanSizingData?.loanEconomics
    : detaileLoanDataToBePicked?.loanEconomics;

  const loanPricing = resolveLoanPricing(loanEconomicsForPricing);
  applyPricingAdjustmentOverrides(loanPricing, pricingAdjustmentOverrides);
  const leverageAdjustments = computeLeverageAdjustments(
    leverageAdjustedMaxOverrides
  );

  const resultObj: any = buildResultObj(
    detaileLoanDataToBePicked,
    loanId,
    loanPricing,
    leverageAdjustments
  );

  const requestBody: any = {
    rulesRequest: {
      ruleAttributs: {
        ruleField: [],
        ruleGroup: [],
        ruleResult: [],
      },
      loanFact: {
        loan: loanObj,
        properties: propObj,
      },
      resultObject: resultObj,
    },
    attributes: ["criticalTestLoanEligibility"],
  };
  applyQuickLoanCharacterization(
    resultObj,
    quickLoanSizingData,
    loanDetails,
    sizingType,
    loanId
  );
  applyQuickPropertyGrossRent(resultObj, quickLoanSizingData, loanDetails);
  applyTestOverrides(resultObj, loanId, testOverridesToApply);

  if (Object.keys(resultObj.loanResult[loanId]).length) {
    requestBody.rulesRequest.resultObject = {
      ...requestBody.rulesRequest.resultObject,
      ...resultObj,
    };
  }
  return requestBody;
};

// A waived test's natural per-test status lives in one of two shapes
// depending on loan type: a single loanResult tree keyed by loanId (Bridge),
// or one loanResult tree per assigned partner under tpResultMap (DSCR). If
// neither shape yields a definite answer, default to "still failing" —
// silently dropping a waiver's pricing/leverage protection would be worse
// than an unnecessary extra override.
function isWaivedTestStillFailing(
  naturalData: any,
  loanId: any,
  testType: string,
  testKey: string,
): boolean {
  const directStatus = naturalData?.loanResult?.[loanId]?.[testType]?.[testKey];
  if (directStatus !== undefined) {
    return String(directStatus).toUpperCase() !== "PASS";
  }

  const tpResultMap = naturalData?.tpResultMap;
  if (tpResultMap && typeof tpResultMap === "object") {
    return Object.values(tpResultMap).some((partnerResult: any) => {
      const partnerLoanResult = partnerResult?.loanResult ?? {};
      const resultKey = Object.keys(partnerLoanResult)[0];
      const status = partnerLoanResult?.[resultKey]?.[testType]?.[testKey];
      return status === undefined || String(status).toUpperCase() !== "PASS";
    });
  }

  return true;
}

// Of every test currently WAIVED via an approved waiver, keeps only the ones
// the natural (unwaived) evaluation still shows failing.
function filterStillFailingOverrides(
  overrides: Record<string, Record<string, string>>,
  naturalData: any,
  loanId: any,
): Record<string, Record<string, string>> {
  const filtered: Record<string, Record<string, string>> = {};
  Object.entries(overrides).forEach(([testType, tests]) => {
    Object.entries(tests).forEach(([testKey, value]) => {
      if (isWaivedTestStillFailing(naturalData, loanId, testType, testKey)) {
        filtered[testType] = { ...filtered[testType], [testKey]: value };
      }
    });
  });
  return filtered;
}

export const fetchGuidelinesAndBuyDownOptions = async (props: any) => {
  const {
    loanDetails,
    quickLoanSizingData,
    detailedLoanSizingData,
    loanId,
    dynamicConfig,
    selectedSizingType,
    isInitialLoad,
    pricingAdjustmentOverrides,
    leverageAdjustedMaxOverrides,
  } = props;
  try {
    // Phase 1: evaluate with no waiver overrides at all, so a test that a
    // recent edit now genuinely resolves shows PASS instead of being masked
    // by a blindly-reapplied WAIVED override forever.
    const naturalReqBody = getRequestBodyForGuidelines(
      loanDetails,
      quickLoanSizingData,
      detailedLoanSizingData,
      loanId,
      dynamicConfig,
      selectedSizingType,
      false,
      isInitialLoad,
      undefined,
      pricingAdjustmentOverrides,
      leverageAdjustedMaxOverrides,
    );
    const naturalResult = await evaluatePartnerGuidelines(naturalReqBody);
    const naturalData = naturalResult?.data ?? {};

    const overriddenTestResults = getOverriddenTestResults(
      loanDetails?.waiverDetails,
      loanId,
    );
    const stillFailingOverrides = filterStillFailingOverrides(
      overriddenTestResults,
      naturalData,
      loanId,
    );

    // Nothing waived is still genuinely failing — the natural result is
    // already correct and a second call would risk relabeling a newly
    // passing test back to WAIVED.
    if (!Object.keys(stillFailingOverrides).length) {
      return naturalData;
    }

    // Phase 2: re-evaluate with the override applied only to tests still
    // genuinely failing, so pricing/leverage keeps reflecting the waiver
    // for those.
    const overriddenReqBody = getRequestBodyForGuidelines(
      loanDetails,
      quickLoanSizingData,
      detailedLoanSizingData,
      loanId,
      dynamicConfig,
      selectedSizingType,
      false,
      isInitialLoad,
      stillFailingOverrides,
      pricingAdjustmentOverrides,
      leverageAdjustedMaxOverrides,
    );
    // A credit-committee-entered finalToorakPrice is a fixed manual price,
    // not something a rate buydown should be computed against — skip
    // generating buydown options for this call when one is present.
    const hasOverriddenFinalToorakPrice = Boolean(
      overriddenReqBody?.rulesRequest?.resultObject?.loanResult?.[loanId]
        ?.loanPricing?.finalToorakPrice
    );
    const overriddenResult = await evaluatePartnerGuidelines(
      overriddenReqBody,
      !hasOverriddenFinalToorakPrice,
    );
    return overriddenResult?.data ?? naturalData;
  } catch (error) {
    console.error(
      "Something went wrong while fetching guidelines and buy down options",
      error
    );
  }
};

type ObjectType = Record<string, any>;

export const getTestTabResults = async (props: any) => {
  try {
    const {
      tempCriticalTests,
      RootContextData,
      loanContextData,
      templateResponseData,
      isDSCR,
      loanData,
      tempLoanResult,
      loanTypeStr,
      partnerId,
      result,
      loanId,
    } = props;
    const { DSCROrgPartnerMapping, dscrPartner, dynamicConfig } =
      RootContextData;
    const {
      setLeverageAndPricingData,
      setTestTabData,
      setFailedFinalRate,
      loanDetails,
      chipsData,
      setChipsData,
      setTemplateResponseData,
      setQuickLoanSizingData,
      pricingAdjustmentOverrides,
      leverageAdjustedMaxOverrides,
    } = loanContextData;

    const testKey = ["logicTests", "finalCalculation", "maxMinParameters"];
    const criticalTestData = { ...(tempCriticalTests ?? {}) };
    let templateResponse = criticalTestData || {};

    try {
      const failTests: ObjectType[] = [];
      const passTests: ObjectType[] = [];
      let failedCountTests: any = 0;

      if (isDSCR) {
        await handleDSCRTests();
      } else {
        await handleNonDSCRTests();
      }

      const normalizedResult: any = normalizeEvaluationResult(
        tempLoanResult,
        loanTypeStr,
        loanData,
        {},
        // fetchData,
        failedCountTests,
        pricingAdjustmentOverrides,
        leverageAdjustedMaxOverrides
      );

      if (
        !templateResponseData ||
        Object.keys(templateResponseData).length === 0
      ) {
        setTemplateResponseData(templateResponse);
      }

      setLeverageAndPricingData(normalizedResult || {});
      setTestTabData(failTests);
      setFailedFinalRate(
        !(
          normalizedResult?.leverageCards?.finalRate ||
          normalizedResult?.leverageCards?.finalPrice
        )
      );

      return failTests;

      // Helper Functions
      async function handleDSCRTests() {
        const tempChipData: string[] = [...chipsData];
        const takeOutPartnerId = partnerId || loanDetails?.takeOutPartner || "";

        updateChipsData(tempChipData, takeOutPartnerId);

        for (const partner of dscrPartner || []) {
          if (result[partner.id]) {
            await processPartnerData(partner.id);
          }
        }

        if (partnerId) {
          failedCountTests += countFailedTests(result[partnerId]?.loanResult);
        }
      }

      async function handleNonDSCRTests() {
        const loanKey = Object.keys(result?.loanResult)[0];
        const guidelineTest = result?.loanResult?.[loanKey]?.guidelineTest ?? {};
        const loanEconomicsTests =
          result?.loanResult?.[loanKey]?.loanEconomics ?? {};
        const tests = { ...guidelineTest, ...loanEconomicsTests };
        // Preserved so findTestWaiver (which matches on
        // loanResult.{loanId}.{testType}.{testKey}) can still identify which
        // testType a given testName belongs to after guidelineTest and
        // loanEconomics are merged into one flat map above.
        const testTypeByName: Record<string, string> = {};
        Object.keys(guidelineTest).forEach((name) => {
          testTypeByName[name] = "guidelineTest";
        });
        Object.keys(loanEconomicsTests).forEach((name) => {
          testTypeByName[name] = "loanEconomics";
        });

        const templateKeys = Object.keys(tests).map(
          (testName) => `testNames.${testName}`
        );
        const reqBody = { templateKeys };
        const partyId = getCookie("partyId") || dynamicConfig?.partyId;

        const publicAPI2Headers = {
          accept: "application/json",
          "kc-realm": dynamicConfig?.ViteAppKcRealm,
        };

        if (!templateResponse || Object.keys(templateResponse).length === 0) {
          templateResponse = await getTemplateResponse(
            partyId,
            reqBody,
            publicAPI2Headers
          );
        }

        processTests(tests, templateResponse?.data, testTypeByName);
      }

      function updateChipsData(
        tempChipData: string[],
        takeOutPartnerId: string
      ) {
        const partnerValues = Object.values(DSCROrgPartnerMapping);

        if (
          DSCROrgPartnerMapping[takeOutPartnerId] &&
          !tempChipData.includes(DSCROrgPartnerMapping[takeOutPartnerId])
        ) {
          const existingChip =
            tempChipData.find((chip) => partnerValues.includes(chip)) || "";
          const chipIndex = tempChipData.indexOf(existingChip);

          if (chipIndex >= 0) {
            tempChipData.splice(
              chipIndex,
              1,
              DSCROrgPartnerMapping[takeOutPartnerId]
            );
          } else {
            tempChipData.push(DSCROrgPartnerMapping[takeOutPartnerId]);
          }

          setChipsData([...new Set(tempChipData ?? [])]);
        }
      }

      async function processPartnerData(partnerIdString: string) {
        const tests = result[partnerIdString]?.loanResult?.[loanId];
        const templateKeys: any = [];
        const testObj: any = {};

        Object.keys(tests).forEach((testName) => {
          if (testKey.includes(testName)) {
            testObj[testName] = tests[testName];
            templateKeys.push(
              ...Object.keys(tests[testName]).map((key) => `testNames.${key}`)
            );
          }
        });

        const reqBody = {
          templateKeys,
        };
        const orgId = getCookie("clientId") || dynamicConfig?.clientId;

        if (
          !templateResponse[partnerIdString] ||
          Object.keys(templateResponse[partnerIdString]).length === 0
        ) {
          templateResponse[partnerIdString] = await getTemplateResponseDSCR(
            partnerIdString,
            orgId,
            reqBody
          );
        }

        processTests(testObj, templateResponse[partnerIdString]?.data);
      }

      function processTests(
        tests: ObjectType,
        templateData: ObjectType,
        testTypeByName: Record<string, string> = {}
      ) {
        Object.entries(tests).forEach(([testName, testStatus]) => {
          const normalizedStatus = String(testStatus).toUpperCase();
          if (normalizedStatus === "FAIL" || normalizedStatus === "WAIVED") {
            failedCountTests += 1;
            failTests.push({
              title: evaluation(templateData[`testNames.${testName}`], {}, ""),
              status: "Failed",
              subText: evaluation(
                templateData[`loanResult.${testName}.message.FAIL`],
                {},
                ""
              ),
              testType: testTypeByName[testName],
              testKey: testName,
            });
          } else if (normalizedStatus === "PASS") {
            passTests.push({
              title: testName,
              status: "Passed",
            });
          }
        });
      }

      function countFailedTests(loanResult: any) {
        return Object.values(loanResult || {}).reduce((count: any, tests) => {
          return (
            count +
            Object.values(tests || {}).filter((status) =>
              ["FAIL", "WAIVED"].includes(String(status).toUpperCase())
            ).length
          );
        }, 0);
      }
    } catch (error) {
      console.error("Error in getTestTabResultData:", error);
    }
  } catch (err) {
    console.error(err);
  }
};

export const convertQuickToDetailedLoanData = (loanData: any) => {
  return {
    borrowerInformation: {
      ...loanData?.borrowerInformation,
    },
    loanInformation: {
      ...loanData?.loanInformation,
      originationDate: loanData?.loanInformation?.expectedClosingDate ? getFormattedDate(loanData?.loanInformation?.expectedClosingDate) : null,
     },
    loanEconomics: {
      ...(loanData?.loanEconomics ?? {}),
      borrowerRequested: loanData?.loanInformation?.borrowerRequested ?? null,
      purchasePrice: loanData?.loanInformation?.purchasePrice ?? null,
      prepayPenaltyType:
        prePayTypeToMonthsMap?.[
          loanData?.loanEconomics?.prepaymentPenaltyTypeMonths ?? 0
        ] ?? null,
    },
    propertyDetails: [
      {
        propertyLocation: loanData?.propertyLocation ?? {},
        propertyInformation: loanData?.propertyInformation ?? {},
        propertyEconomics: {
          ...(loanData?.propertyEconomics ?? {}),
          asIsValue: loanData?.propertyInformation?.asIsValue,
          purchasePrice: loanData?.loanInformation?.purchasePrice,
        },
        unitInformation: [{}],
      },
    ],
  };
};

export const formatCurrency = (
  value: number | string,
  noOfDigits?: number
): string => {
  const numberValue = typeof value === "string" ? value : `${value}`;
  if (isNaN(Number(numberValue))) {
    return numberValue;
  }
  const data = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: noOfDigits ?? 4,
  }).format(Number(numberValue));

  return data;
};

export const prePayTypeToMonthsMap: any = {
  60: "5/4/3/2/1",
  48: "4/3/2/1",
  36: "3/2/1",
  24: "2/1",
  12: "1",
  0: "0",
};

export function clearAllData(loansContext: any) {
  const {
    setChipsData,
    setLoanFormData,
    setLoanDetailsConfig,
    setPropertyDetailsConfig,
    setBorrowerInformationConfig,
    setLoanDetails,
    setPropertyDetails,
    setDefaultSelectedDataView,
    setExpandLeverageAndTests,
    setShowSnackBar,
    setLeverageTestsConfig,
    setLoanFormErrors,
    setPropFormErrors,
    setLeverageAndPricingData,
    setPropertyDetailsTemplate,
    setPropertyDetailsConfigTemplate,
    setUnitInfoConfigTemplate,
    setUnitInfoTemplate,
    setTestTabData,
    setAutoCalculatedLoanResults,
    setAutoCalculatedPropResults,
    setCondoEligibilityConfig,
    setCondoEligibility,
    setCondoEligibilityFormData,
    setAddressInputValue,
    setAddressOptions,
    setZipCodeInputValue,
    setZipCodeOptions,
    setFailedFinalRate,
    setFetchData,
    setRatesAsOfDate,
    setDebouncedValue,
    setNoAssignedPartner,
    setAssignedPartner,
    setIsUnitExpanded,
    setAutoCalculatedFields,
    setExpandAvailableProgramsView,
    setEligiblePartnerResults,
    setPartnersResults,
    setLoansApiResponse,
    setRulesResponse,
    setCriticalTests,
    setTemplateResponseData,
    setView,
    setSizingType,
    setQuickLoanSizingData,
    setDetailedLoanSizingData,
    setFieldConfig,
    setDetailedLoanSizingErrors,
    setNewDebouncedValue,
    setCurrentSelectedBuyDownOption,
    setBuyDownOptions,
    setCurrentSelectedInterestRate,
    setLatestTermSheetVersion,
  } = loansContext;

  setLoanFormData({});
  setAutoCalculatedFields({});
  setLoanDetailsConfig({});
  setPropertyDetailsConfig([]);
  setBorrowerInformationConfig({});
  setLoanDetails({});
  setPropertyDetails([]);
  setDefaultSelectedDataView("");
  setExpandLeverageAndTests(false);
  setExpandAvailableProgramsView(false);
  setShowSnackBar(false);
  setLeverageTestsConfig([]);
  setLoanFormErrors({});
  setPropFormErrors([]);
  setLeverageAndPricingData({});
  setPropertyDetailsTemplate({});
  setPropertyDetailsConfigTemplate({});
  setUnitInfoConfigTemplate({});
  setUnitInfoTemplate([]);
  setTestTabData([]);
  setAutoCalculatedLoanResults({});
  setAutoCalculatedPropResults([]);
  setCondoEligibilityConfig({});
  setCondoEligibility(false);
  setCondoEligibilityFormData(sampleCondoEligibility);
  setAddressInputValue("");
  setAddressOptions([]);
  setZipCodeInputValue("");
  setZipCodeOptions([]);
  setFailedFinalRate(false);
  setFetchData({});
  setRatesAsOfDate("");
  setDebouncedValue(null);
  setNoAssignedPartner("");
  setAssignedPartner("");
  setIsUnitExpanded({});
  setChipsData([]);
  setEligiblePartnerResults({});
  setPartnersResults({});
  setLoansApiResponse({});
  setRulesResponse({});
  setCriticalTests({});
  setTemplateResponseData([]);
  setSizingType("");
  setQuickLoanSizingData({});
  setDetailedLoanSizingData(intialDetailedLoanSizingState);
  setView("");
  setFieldConfig({});
  setDetailedLoanSizingErrors({});
  setBuyDownOptions([]);
  setCurrentSelectedBuyDownOption({});
  setNewDebouncedValue(null);
  setCurrentSelectedInterestRate({});
  setLatestTermSheetVersion("");
}

export function validateNumberField(input: any) {
  // Check if the input contains alphabetic characters
  const hasAlphabets = /[a-zA-Z]/.test(input);

  if (hasAlphabets) {
    return true;
  }

  // Check if input is a valid number
  if (isNaN(+input) || input.trim() === "" || +input < 0) {
    return true;
  }

  return false;
}
